/**
 * The input lock on an answer that just appeared (#660).
 *
 * One tap moves the check to the next line, so the next line's "Yes" can land under the finger that just tapped. A
 * confirmation the learner never meant is a false accept, the asymmetric failure: it manufactures evidence that was never
 * earned and nothing downstream can detect it. So an answer that has just appeared ignores activation for a short while,
 * and ignores any press that BEGAN before the lock ended, even if it is released later:
 *
 *  - a click while the lock timer is running does nothing;
 *  - a pointer press whose `pointerdown` came before the lock ended never counts, however late it is released;
 *  - a held Enter or Space (`event.repeat`) never activates, and neither does a key press that began before the lock ended.
 *
 * The lock is a real timer, not a clock reading: the visual tests freeze `Date`, and `prefers-reduced-motion` must not
 * skip it, because it is safety, not motion. "Began before the lock ended" is counted in presses, not milliseconds: the
 * lock records how many presses had been seen when its timer ran out, and a press counts only if it is a later one.
 *
 * It is shown nowhere (no disabled look) and costs nothing to a learner who is reading. "Appeared" is the card becoming the
 * step on screen (`AnswerLockScope`), the row mounting, or, for a button that is disabled until it can be pressed (the
 * page's confirm), its becoming enabled.
 */

import {
  createContext,
  useContext,
  useLayoutEffect,
  useRef,
  type MutableRefObject,
} from "react";

export const ANSWER_LOCK_MS = 450;
let lockMs = ANSWER_LOCK_MS;

/** A test seam: the suite sets 0 so a test can tap line after line; the lock's own tests set a long, known lock. */
export function setAnswerLockMs(ms: number): void {
  lockMs = ms;
}

/** Presses seen so far, and the one that is waiting for its click. Kept for the whole page: a press may begin anywhere. */
const presses = {
  pointers: 0,
  keys: 0,
  /** The pointer press and key press the next click comes from; spent by that click. */
  pointer: undefined as number | undefined,
  key: undefined as number | undefined,
  repeating: false,
};
let installed = false;

function installPressTracking(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  window.addEventListener(
    "pointerdown",
    () => {
      presses.pointers += 1;
      presses.pointer = presses.pointers;
    },
    true,
  );
  window.addEventListener(
    "keydown",
    (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      if (event.repeat) {
        presses.repeating = true;
        return;
      }
      presses.repeating = false;
      presses.keys += 1;
      presses.key = presses.keys;
    },
    true,
  );
  window.addEventListener(
    "keyup",
    (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      // Space clicks after its keyup has been dispatched, so the hold is cleared a tick later.
      setTimeout(() => {
        presses.repeating = false;
      }, 0);
    },
    true,
  );
}

interface Lock {
  locked: boolean;
  /** How many pointer and key presses had been seen when the lock ended: a press counts only if it came after. */
  pointers: number;
  keys: number;
  timer: ReturnType<typeof setTimeout> | undefined;
}

const newLock = (): Lock => ({
  locked: true,
  pointers: 0,
  keys: 0,
  timer: undefined,
});

function release(lock: Lock): void {
  lock.locked = false;
  lock.pointers = presses.pointers;
  lock.keys = presses.keys;
  lock.timer = undefined;
}

/** Locked while `active` is false; each time it turns true, locked again until the timer runs out. */
function useLock(active: boolean): MutableRefObject<Lock> {
  const lock = useRef<Lock>(newLock());
  useLayoutEffect(() => {
    installPressTracking();
    const state = lock.current;
    clearTimeout(state.timer);
    state.timer = undefined;
    state.locked = true;
    if (!active) return;
    if (lockMs <= 0) release(state);
    else state.timer = setTimeout(() => release(state), lockMs);
    return () => {
      clearTimeout(state.timer);
      state.timer = undefined;
    };
  }, [active]);
  return lock;
}

const ScopeContext = createContext<MutableRefObject<Lock> | undefined>(
  undefined,
);

/** Wraps one card. While `active` is false (the card is hidden) nothing inside can be activated; turning true is appearing. */
export function AnswerLockScope({
  active,
  children,
}: {
  readonly active: boolean;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  const lock = useLock(active);
  return <ScopeContext.Provider value={lock}>{children}</ScopeContext.Provider>;
}

/**
 * Whether an activation may count. `enabled` is for a button that is disabled until it can be pressed: becoming enabled
 * counts as appearing.
 */
export function useAnswerGuard(
  enabled = true,
): (event: React.SyntheticEvent) => boolean {
  const scope = useContext(ScopeContext);
  const own = useLock(enabled);

  return (event) => {
    const locks = [own.current, ...(scope === undefined ? [] : [scope.current])];
    const native = event.nativeEvent;
    const pointer = native instanceof MouseEvent && native.detail > 0;
    // Each press is spent by the click it produced, whatever the answer.
    const press = pointer ? presses.pointer : presses.key;
    const repeating = presses.repeating;
    if (pointer) presses.pointer = undefined;
    else presses.key = undefined;

    if (locks.some((lock) => lock.locked)) return false;
    if (!pointer && repeating) return false;
    if (press === undefined) return true;
    return locks.every(
      (lock) => press > (pointer ? lock.pointers : lock.keys),
    );
  };
}

/** A button that ignores an activation the lock refuses: the page's confirm, drawn right after the last line. */
export function GuardedButton({
  onActivate,
  disabled = false,
  className,
  children,
}: {
  readonly onActivate: () => void;
  readonly disabled?: boolean;
  readonly className?: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  const allows = useAnswerGuard(!disabled);
  return (
    <button
      type="button"
      className={className}
      disabled={disabled}
      onClick={(event) => {
        if (allows(event)) onActivate();
      }}
    >
      {children}
    </button>
  );
}
